(() => {
  'use strict';

  const ROOT_SELECTOR = '.product-details-page .coupon-carousel-v6';
  const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [role="button"], .cc6-code';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let generatedId = 0;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const initCarousel = (root) => {
    if (!(root instanceof Element)) return;

    const scrollEl = root.querySelector('.cc6-scroll');
    const track = root.querySelector('.cc6-track');
    const thumb = root.querySelector('.cc6-thumb');
    const fadeRight = root.querySelector('.cc6-fade-right');
    const arrow = root.querySelector('.cc6-hint-arrow');

    if (!scrollEl || !track || !thumb || scrollEl.dataset.zyvOffersReady === 'true') return;
    scrollEl.dataset.zyvOffersReady = 'true';

    if (!scrollEl.id) {
      generatedId += 1;
      scrollEl.id = `zyvira-offers-scroll-${generatedId}`;
    }

    scrollEl.setAttribute('tabindex', '0');
    scrollEl.setAttribute('role', 'region');
    scrollEl.setAttribute('aria-label', 'Available offers. Scroll horizontally to see more offers.');

    track.setAttribute('role', 'scrollbar');
    track.setAttribute('tabindex', '0');
    track.setAttribute('aria-controls', scrollEl.id);
    track.setAttribute('aria-orientation', 'horizontal');
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', '100');
    track.setAttribute('aria-label', 'Available offers scroll position');

    if (arrow) {
      arrow.setAttribute('role', 'button');
      arrow.setAttribute('tabindex', '0');
      arrow.setAttribute('aria-controls', scrollEl.id);
      arrow.setAttribute('aria-label', 'Show more available offers');
    }

    const getMaxScroll = () => Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);

    const update = () => {
      const maxScroll = getMaxScroll();
      const hasOverflow = maxScroll > 2;
      const ratio = hasOverflow ? clamp(scrollEl.scrollLeft / maxScroll, 0, 1) : 0;
      const thumbWidth = hasOverflow
        ? Math.max((scrollEl.clientWidth / scrollEl.scrollWidth) * 100, 15)
        : 100;
      const maxLeft = Math.max(0, 100 - thumbWidth);
      const atEnd = !hasOverflow || ratio > 0.985;

      track.classList.toggle('hide', !hasOverflow);
      fadeRight?.classList.toggle('hide', atEnd);
      thumb.style.width = `${thumbWidth}%`;
      thumb.style.left = `${ratio * maxLeft}%`;
      track.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
      track.setAttribute('aria-disabled', String(!hasOverflow));

      if (arrow) {
        arrow.setAttribute('tabindex', atEnd ? '-1' : '0');
        arrow.setAttribute('aria-hidden', String(atEnd));
      }
    };

    let updateFrame = 0;
    const scheduleUpdate = () => {
      window.cancelAnimationFrame(updateFrame);
      updateFrame = window.requestAnimationFrame(update);
    };

    const getStep = () => {
      const card = scrollEl.querySelector('.cc6-card');
      if (!card) return Math.max(220, scrollEl.clientWidth * 0.72);
      const styles = window.getComputedStyle(scrollEl);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const scrollByStep = (direction) => {
      const maxScroll = getMaxScroll();
      if (maxScroll <= 2) return;
      const next = clamp(scrollEl.scrollLeft + (getStep() * direction), 0, maxScroll);
      scrollEl.scrollTo({
        left: next,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    const normalizeWheelDelta = (event) => {
      let delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (event.deltaMode === 1) delta *= 16;
      if (event.deltaMode === 2) delta *= scrollEl.clientWidth;
      return delta;
    };

    const handleWheel = (event) => {
      const maxScroll = getMaxScroll();
      const delta = normalizeWheelDelta(event);

      /* Always block the legacy carousel wheel handler, while allowing normal
         page scrolling when this carousel has reached the requested edge. */
      event.stopImmediatePropagation();

      if (maxScroll <= 2 || Math.abs(delta) < 0.5) return;

      const atStart = scrollEl.scrollLeft <= 1;
      const atEnd = scrollEl.scrollLeft >= maxScroll - 1;
      const canMove = delta > 0 ? !atEnd : !atStart;
      if (!canMove) return;

      event.preventDefault();
      scrollEl.scrollLeft = clamp(scrollEl.scrollLeft + delta, 0, maxScroll);
      scheduleUpdate();
    };

    scrollEl.addEventListener('wheel', handleWheel, { capture: true, passive: false });
    scrollEl.addEventListener('scroll', scheduleUpdate, { passive: true });

    let drag = null;
    let suppressClickUntil = 0;

    const finishDrag = (event) => {
      if (!drag || (event && event.pointerId !== drag.pointerId)) return;

      if (drag.moved) suppressClickUntil = performance.now() + 350;
      scrollEl.classList.remove('is-zyv-dragging');

      try {
        if (scrollEl.hasPointerCapture?.(drag.pointerId)) {
          scrollEl.releasePointerCapture(drag.pointerId);
        }
      } catch (error) {
        // Pointer capture can already be released by the browser.
      }

      drag = null;
      scheduleUpdate();
    };

    scrollEl.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if (event.target.closest(INTERACTIVE_SELECTOR)) return;
      if (getMaxScroll() <= 2) return;

      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startLeft: scrollEl.scrollLeft,
        moved: false
      };

      try {
        scrollEl.setPointerCapture(event.pointerId);
      } catch (error) {
        // Dragging still works without pointer capture in older browsers.
      }
    });

    scrollEl.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const distance = event.clientX - drag.startX;

      if (!drag.moved && Math.abs(distance) < 4) return;
      if (!drag.moved) {
        drag.moved = true;
        scrollEl.classList.add('is-zyv-dragging');
      }

      event.preventDefault();
      scrollEl.scrollLeft = clamp(drag.startLeft - distance, 0, getMaxScroll());
      scheduleUpdate();
    }, { passive: false });

    scrollEl.addEventListener('pointerup', finishDrag);
    scrollEl.addEventListener('pointercancel', finishDrag);
    scrollEl.addEventListener('lostpointercapture', finishDrag);

    scrollEl.addEventListener('click', (event) => {
      if (performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    const handleArrowClick = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      scrollByStep(1);
    };

    arrow?.addEventListener('click', handleArrowClick, true);
    arrow?.addEventListener('keydown', (event) => {
      if (!['Enter', ' ', 'ArrowRight', 'ArrowLeft'].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      scrollByStep(event.key === 'ArrowLeft' ? -1 : 1);
    }, true);

    scrollEl.addEventListener('keydown', (event) => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      if (event.key === 'Home') {
        scrollEl.scrollTo({ left: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      } else if (event.key === 'End') {
        scrollEl.scrollTo({ left: getMaxScroll(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      } else {
        scrollByStep(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });

    let trackDrag = null;

    const seekTrack = (clientX) => {
      const maxScroll = getMaxScroll();
      if (maxScroll <= 2) return;

      const rect = track.getBoundingClientRect();
      const thumbPixels = thumb.getBoundingClientRect().width;
      const usableWidth = Math.max(1, rect.width - thumbPixels);
      const ratio = clamp((clientX - rect.left - (thumbPixels / 2)) / usableWidth, 0, 1);
      scrollEl.scrollLeft = ratio * maxScroll;
      scheduleUpdate();
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || getMaxScroll() <= 2) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      trackDrag = { pointerId: event.pointerId };
      try {
        track.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture is optional.
      }
      seekTrack(event.clientX);
    }, true);

    track.addEventListener('pointermove', (event) => {
      if (!trackDrag || event.pointerId !== trackDrag.pointerId) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      seekTrack(event.clientX);
    }, { capture: true, passive: false });

    const finishTrackDrag = (event) => {
      if (!trackDrag || event.pointerId !== trackDrag.pointerId) return;
      event.stopImmediatePropagation();
      trackDrag = null;
    };

    track.addEventListener('pointerup', finishTrackDrag, true);
    track.addEventListener('pointercancel', finishTrackDrag, true);
    track.addEventListener('mousedown', (event) => {
      if (!trackDrag) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    track.addEventListener('keydown', (event) => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (event.key === 'Home') {
        scrollEl.scrollTo({ left: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      } else if (event.key === 'End') {
        scrollEl.scrollTo({ left: getMaxScroll(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      } else {
        const direction = ['ArrowLeft', 'PageUp'].includes(event.key) ? -1 : 1;
        scrollByStep(direction);
      }
    }, true);

    const resizeObserver = 'ResizeObserver' in window
      ? new ResizeObserver(scheduleUpdate)
      : null;
    resizeObserver?.observe(root);
    resizeObserver?.observe(scrollEl);

    update();
  };

  const initWithin = (scope = document) => {
    if (scope instanceof Element) {
      if (scope.matches(ROOT_SELECTOR)) initCarousel(scope);
      const closest = scope.closest(ROOT_SELECTOR);
      if (closest) initCarousel(closest);
    }
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initCarousel);
  };

  const initialize = () => {
    initWithin(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) initWithin(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
